import { ConnectionResponse } from "@pufflig/prompt-studio-types";
import { safeParse, safeStringify } from "@pufflig/prompt-tools";
import { PromptbookEditorRef } from "@pufflig/promptbook-editor";
import { PromptbookWebRuntime, usePromptbookRuntime } from "@pufflig/promptbook-runtime";
import { useEffect } from "react";
import { handleRunbookResponse } from "./handleRunbookResponse";
import { handleSQLAgentResponse } from "./handleSQLAgentResponse";
import { handleVisualizationResponse } from "./handleVisualizationResponse";
import { handleLocalSQLInterrupt } from "./interrupts/handleLocalSQLInterrupt";
import { handleRunbookInterrupt } from "./interrupts/handleRunbookInterrupt";
import { finishThread } from "./threads/threads";

export const createHandleMessage =
  (runtime: PromptbookWebRuntime, connection: WebSocket, ref: React.RefObject<PromptbookEditorRef>) =>
  async (event: MessageEvent) => {
    const parsedData = safeParse(event.data) as ConnectionResponse;

    if (parsedData.type === "runbook_response") {
      if (parsedData.interrupt) {
        await handleRunbookInterrupt(parsedData, runtime.db, connection, ref);
        return;
      }

      await handleRunbookResponse(parsedData, runtime, connection, ref);
    }

    if (parsedData.type === "sql_agent_response") {
      if (parsedData.interrupt) {
        await handleLocalSQLInterrupt(parsedData.threadId, parsedData.interrupt, runtime, connection);
        return;
      }

      await handleSQLAgentResponse(parsedData, runtime);
    }

    if (parsedData.type === "visualization_agent_response") {
      await handleVisualizationResponse(parsedData);
    }

    if (parsedData.type === "error") {
      if (parsedData.threadId) {
        console.log(`Error in thread ${parsedData.threadId}:`, parsedData.error);
        finishThread(parsedData.threadId, safeStringify(parsedData.error as any), true);
      }
    }
  };

/**
 * Listen for state updates from the WebSocket connection and update the Promptbook Editor.
 *
 * @param connection
 * @param ref
 */
export const useStateUpdate = (connection: WebSocket | null, ref: React.RefObject<PromptbookEditorRef>) => {
  const { runtime } = usePromptbookRuntime();

  useEffect(() => {
    if (!connection || !runtime) return;

    const handleMessage = createHandleMessage(runtime, connection, ref);

    connection.addEventListener("message", handleMessage);

    return () => {
      connection.removeEventListener("message", handleMessage);
    };
  }, [!!connection, runtime]);
};
